import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  isPaintRegion,
  isWallTileRegion,
} from '@shared-lib/estimate-pricing/utils/roomTakeOffs';
import { getTextureById } from '../services/unifiedTextureService';

// The texture ids of every wall region that renders a finish of its own (not
// paint, not an unsaved draft), sorted so the list says the same thing
// whatever order the rooms come in.
const regionTextureIds = (rooms) => {
  const ids = new Set();
  for (const room of rooms || []) {
    for (const wall of room?.walls || []) {
      for (const region of wall?.texture?.regions || []) {
        if (isWallTileRegion(region)) ids.add(region.id);
      }
    }
  }
  return [...ids].sort();
};

// A texture's finish type, or null when it could not be read: a failed load
// settles too, so it never holds `ready` back.
const loadType = async (id) => {
  try {
    const doc = await getTextureById(id);
    return doc?.type ?? null;
  } catch {
    return null;
  }
};

/**
 * The finish type of each wall region in `rooms`, for the baseboard rule
 * (`shared-lib/millwork`): a tile, slab, panel or wood region reaching the
 * floor stops baseboard. The estimate reads the type from its texture cache;
 * the 3D view has no such cache, so this loads each region's `unifiedTextures`
 * doc once and says when it has them all. A caller must not draw baseboard
 * before `ready`, since a region whose type is still loading would read as
 * stopping nothing.
 *
 * @param {Object[]} rooms
 * @returns {{ ready: boolean, materialTypeOf: (region) => string|null }}
 *   `materialTypeOf` is `'paint'` for a paint region, the loaded doc's `type`
 *   for a texture, null otherwise; `ready` is false until every load settled
 */
export const useRegionMaterialTypes = (rooms) => {
  // a string, so the load runs again only when the list itself changes
  const idsKey = useMemo(
    () => JSON.stringify(regionTextureIds(rooms)),
    [rooms]
  );
  const [types, setTypes] = useState(() => new Map());
  const typesRef = useRef(types);
  typesRef.current = types;

  useEffect(() => {
    const wanted = JSON.parse(idsKey);
    const missing = wanted.filter((id) => !typesRef.current.has(id));
    if (missing.length === 0) return undefined;
    // a list the rooms have since moved on from must not land last
    let superseded = false;
    Promise.all(missing.map((id) => loadType(id).then((t) => [id, t]))).then(
      (loaded) => {
        if (superseded) return;
        setTypes((prev) => {
          const next = new Map(prev);
          for (const [id, t] of loaded) next.set(id, t);
          return next;
        });
      }
    );
    return () => {
      superseded = true;
    };
  }, [idsKey]);

  const ready = useMemo(
    () => JSON.parse(idsKey).every((id) => types.has(id)),
    [idsKey, types]
  );

  const materialTypeOf = useCallback(
    (region) => {
      if (isPaintRegion(region)) return 'paint';
      return types.get(region?.id) ?? null;
    },
    [types]
  );

  return { ready, materialTypeOf };
};

export default useRegionMaterialTypes;
